import { GAME_RESULT, MATCH_STATUS, TOURNAMENT_GAME_RESULT, } from "../../constants";
import { createGamesFromPairings } from "../utils/gameUtils";
import { RoundStatus, } from "./interfaces/ITournamentRoundService";
export class TournamentRoundService {
    repository;
    gameService;
    playerService;
    standingsCalculator;
    constructor(repository, gameService, playerService, standingsCalculator) {
        this.repository = repository;
        this.gameService = gameService;
        this.playerService = playerService;
        this.standingsCalculator = standingsCalculator;
    }
    async getCurrentRoundNumber(tournamentId) {
        const tournament = await this.repository.getTournament(tournamentId);
        return tournament.currentRound;
    }
    getRoundStatus(round, games) {
        const roundGames = games.filter((g) => g.roundNumber === round.roundNumber);
        if (round.standingsAfter && round.finishTime) {
            return RoundStatus.FINISHED;
        }
        if (round.finishTime) {
            return RoundStatus.FINALIZED;
        }
        if (!roundGames.length) {
            return RoundStatus.CREATED;
        }
        const hasStartedGames = roundGames.some((game) => !this.gameService.isByeGame(game) &&
            game.matchRef &&
            game.matchRef.startTime);
        if (!hasStartedGames) {
            return RoundStatus.PAIRED;
        }
        const isComplete = this.isRoundComplete(round, games);
        return isComplete ? RoundStatus.COMPLETED : RoundStatus.STARTED;
    }
    async createRound(tournamentId, roundNumber) {
        const roundData = {
            id: roundNumber.toString(),
            roundNumber,
            standings: [],
            standingsAfter: null,
            startTime: null,
            finishTime: null,
        };
        await this.repository.createRound(tournamentId, roundData);
    }
    async pairRound(tournamentId, roundNumber, pairings) {
        const round = await this.repository.getRound(tournamentId, roundNumber);
        if (!round) {
            throw new Error(`Round ${roundNumber} not found`);
        }
        // Check if this is an offline class tournament by checking repository type
        const isOfflineClassTournament = this.repository.constructor.name === "OfflineClassTournamentRepository";
        const games = createGamesFromPairings(pairings, round, !isOfflineClassTournament);
        this.saveRoundGames(tournamentId, games);
    }
    async saveRoundGames(tournamentId, games) {
        await this.gameService.saveRoundGames(tournamentId, games);
    }
    async startRound(tournamentId, roundNumber) {
        await this.repository.updateRoundStartTime(tournamentId, roundNumber, new Date());
        await this.repository.updateCurrentRound(tournamentId, roundNumber);
        await this.startUnstartedRoundGames(tournamentId, roundNumber);
        // TODO: emit result for pipelineService
    }
    async startUnstartedRoundGames(tournamentId, roundNumber) {
        const players = await this.repository.getPlayers(tournamentId);
        const games = await this.gameService.getRoundGames(tournamentId, roundNumber);
        const roundGamesToStart = games.filter((g) => !g.matchRef && g.whiteUsername && g.blackUsername);
        const result = {
            started: 0,
            startedGames: [],
            failed: 0,
            errors: [],
        };
        await Promise.all(roundGamesToStart.map(async (game) => {
            try {
                const whitePlayer = players.find((p) => p.username === game.whiteUsername);
                const blackPlayer = players.find((p) => p.username === game.blackUsername);
                const needsMatch = await this.gameService.startRoundGame(tournamentId, game, whitePlayer, blackPlayer);
                if (!needsMatch)
                    return; // game was already started or is AI vs AI
                result.started++;
                result.startedGames.push({ game, whitePlayer, blackPlayer });
            }
            catch (err) {
                result.errors.push(err);
                result.failed++;
            }
        }));
    }
    async forfeitRound(tournamentId, roundNumber) {
        const roundGames = await this.gameService.getRoundGames(tournamentId, roundNumber);
        await Promise.all(roundGames.map((game) => this.gameService.forfeitIfNotCommenced(game)));
        // Get updated games and count remaining live games
        const updatedGames = await this.repository.getGames(tournamentId);
        return updatedGames.filter((game) => game.roundNumber === roundNumber && game.matchRef && !game.finishTime).length;
    }
    async abortRound(tournamentId, roundNumber) {
        const roundGames = await this.gameService.getRoundGames(tournamentId, roundNumber);
        await Promise.all(roundGames.map(async (game) => {
            const updatedMatch = {
                status: MATCH_STATUS.ABORTED,
                result: GAME_RESULT.ABORT,
                player_1_score: null,
                player_2_score: null,
            };
            if (game.matchRef) {
                const currentMatch = await this.repository.getMatch(game.matchGUID);
                if (currentMatch && !currentMatch.endedDate) {
                    updatedMatch.endedDate = new Date();
                }
            }
            return this.repository.updateMatch(game.matchGUID, updatedMatch);
        }));
    }
    isRoundComplete(round, games) {
        const roundGames = games.filter((g) => g.roundNumber === round.roundNumber);
        return roundGames.every((game) => this.gameService.isByeGame(game) ||
            (game.whiteResult !== TOURNAMENT_GAME_RESULT.UNFINISHED &&
                game.blackResult !== TOURNAMENT_GAME_RESULT.UNFINISHED));
    }
    async fixMismatchedGames(tournamentId, roundNumber) {
        const roundGames = await this.gameService.getRoundGames(tournamentId, roundNumber);
        let mismatchedCount = 0;
        for (const game of roundGames) {
            const wasFixed = await this.gameService.fixResultIfMismatched(tournamentId, game);
            if (wasFixed)
                ++mismatchedCount;
        }
        console.log(`Resolve ${mismatchedCount} game-results`);
        return mismatchedCount;
    }
    async finishRound(tournamentId, roundNumber) {
        const logs = [];
        // Get all necessary data
        const [tournament, players, games, rounds] = await Promise.all([
            this.repository.getTournament(tournamentId),
            this.repository.getPlayers(tournamentId),
            this.repository.getGames(tournamentId),
            this.repository.getRounds(tournamentId),
        ]);
        // Finalize round and resolve any mismatched game results
        const mismatchedCount = await this.fixMismatchedGames(tournamentId, roundNumber);
        logs.push(`Resolve ${mismatchedCount} game-results`);
        // Refresh games list if there were mismatches
        let updatedGames = games;
        if (mismatchedCount > 0) {
            updatedGames = await this.gameService.getRoundGames(tournamentId, roundNumber);
        }
        // Calculate and save standings
        const standings = this.standingsCalculator.calculateStandings(tournament, players, updatedGames);
        await this.saveStandings(tournamentId, roundNumber, rounds, standings);
        logs.push("Save standings");
        if (tournament.currentRound === tournament.totalRounds) {
            await this.repository.updateCurrentRound(tournamentId, tournament.totalRounds + 1);
            logs.push(`Set currentRound = ${tournament.totalRounds + 1}`);
            logs.push("End automation");
        }
        else {
            const forfeitedPlayersCount = await this.playerService.handleForfeitedPlayers(tournamentId, roundNumber);
            logs.push(`Unparticipate ${forfeitedPlayersCount} forfeit Loss players`);
        }
        // TODO: emit ROUND_FINISHED event to be handle by TournamentManager
        return { status: "finished", logs };
    }
    async unfinishRound(tournamentId, roundNumber) {
        const tournament = await this.repository.getTournament(tournamentId);
        // If this is the last round and tournament is finished, unfinish the tournament
        if (roundNumber === tournament.totalRounds &&
            tournament.currentRound > tournament.totalRounds) {
            await this.repository.updateCurrentRound(tournamentId, roundNumber);
        }
        else if (tournament.currentRound !== roundNumber) {
            // Next round has started, can't unfinish this round
            throw new Error(`Cannot unfinish round ${roundNumber}: next round has already started`);
        }
        // Remove the finish time from the round
        await this.repository.updateRoundFinishTime(tournamentId, roundNumber, null);
    }
    async updateStandings(tournamentId, roundNumber) {
        // Get all necessary data
        const [tournament, players, rounds] = await Promise.all([
            this.repository.getTournament(tournamentId),
            this.repository.getPlayers(tournamentId),
            this.repository.getRounds(tournamentId),
        ]);
        // Get all games up to this round for standings calculation
        const allGames = await this.repository.getGames(tournamentId);
        const gamesUpToRound = allGames.filter((g) => g.roundNumber <= roundNumber);
        // Calculate standings using the same logic as finishRound
        const standings = this.standingsCalculator.calculateStandings(tournament, players, gamesUpToRound);
        // Save standings for this round
        await this.saveStandings(tournamentId, roundNumber, rounds, standings);
    }
    async saveStandings(tournamentId, roundNumber, rounds, standings) {
        // Update current round final standings
        await this.repository.updateRoundFinalStandings(tournamentId, roundNumber, standings);
        // Update next round's initial standings if there is a next round
        if (roundNumber < rounds.length) {
            await this.repository.updateNextRoundInitialStandings(tournamentId, roundNumber + 1, standings);
        }
    }
}
